"use client";

import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Button,
  createTheme,
  ThemeProvider,
  Autocomplete,
  Typography,
  Chip,
  alpha,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import ReplayIcon from "@mui/icons-material/Replay";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import BarcodeIcon from "@mui/icons-material/QrCode";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChannelIcon from "@mui/icons-material/Tv";
import TuneIcon from "@mui/icons-material/Tune";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import CustomPagination from "./CustomPagination"; // Componente de paginación personalizado.
import { ErpContext } from "../../context/ErpContext";
import GraficasDetallesVentasModal from "./graficas/graficas-detalles-ventas-modal";
import ModalDetalleCuentaCorriente from "./graficas/modal-detalle-cuenta-corriente";

// Enhanced theme with more modern styling
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4a4a4e",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.08),
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "#fff",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            padding: "0 16px",
            height: "56px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "white !important",
            fontWeight: 600,
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "0 8px",
          },
          "& .MuiDataGrid-menuIconButton": {
            color: "rgba(255, 255, 255, 0.7) !important",
            "&:hover": {
              color: "white !important",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "white !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
            padding: "0 16px",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            },
            "&:hover": {
              backgroundColor: "rgba(66, 153, 225, 0.08)",
              transition: "background 0.2s ease",
            },
            transition: "background 0.2s ease",
            cursor: "pointer",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#fff",
            borderTop: "1px solid rgba(224, 224, 224, 0.5)",
            display: "none !important",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#a1a1a1",
            },
          },
        },
      },
    },
  },
});

const TablaDetallesVentas = () => {
  const {
    getTransaccionItemByTipoTransaccion,
    getTransaccionItemByTipoTransaccionAllData,
    getEntidadesClientesTresLetras,
    getItemTresLetras,
    itemTresLetras,
    entidadesClientesTresLetras,
    arrayItemTresLetras,
    getUbicacion,
    ubicaciones,
    isNavbarCollapsed,
    getParametrosGlobales,
    getCanalEntidad,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    getItemUbicacionFilter,
    getCuentaCorrienteCuotas,
  } = useContext(ErpContext);
  const [rows, setRows] = useState([]);
  const [filterCode, setFilterCode] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isFirstRender = useRef(true);
  const [searchText, setSearchText] = useState("");
  const [searchTextCliente, setSearchTextCliente] = useState("");
  const [dniCliente, setDniCliente] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedUbicacion, setSelectedUbicacion] = useState(null);
  const [ubicacionesFiltradas, setUbicacionesFiltradas] = useState([]);
  const [filtersActive, setFiltersActive] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [idTransaccionSearch, setIdTransaccionSearch] = useState("");
  
  // Estados para canal de entidad
  const [canalesEntidades, setCanalesEntidades] = useState([])
  const [selectedCanalEntidad, setSelectedCanalEntidad] = useState(null)
  
  // Estados para filtros adicionales
  const [masFiltrosActive, setMasFiltrosActive] = useState(false)

  // Estados para gráficas
  const [openGraficasModal, setOpenGraficasModal] = useState(false);
  const [loadingGraficas, setLoadingGraficas] = useState(false);
  const [datosGraficas, setDatosGraficas] = useState([]);
  const [openModalVenta, setOpenModalVenta] = useState(false);
  const [selectedCuentaCorriente, setSelectedCuentaCorriente] = useState(null);

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setOpenModalVenta(false);
    setSelectedCuentaCorriente(null);
  };

  // Columnas generadas dinámicamente usando useMemo
  const columns = useMemo(() => {
    const baseColumns = [
    // Oculta columna "Nro Venta"
    // {
    //   field: "ventaId",
    //   headerName: "Nro Venta",
    //   headerAlign: "center",
    //   flex: 0.7,
    //   minWidth: 70,
    //   renderCell: (params) => (
    //     <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
    //       <Typography variant="body2">{params.value}</Typography>
    //     </Box>
    //   ),
    // },
    {
      field: "fecha",
      headerName: "Fecha",
      headerAlign: "center",
      flex: 0.8,
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarTodayIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      headerAlign: "center",
      type: "number",
      flex: 0.8,
      width: 150,
      renderCell: (params) => (
        <Chip
          icon={<AttachMoneyIcon fontSize="small" />}
          label={params.value?.toLocaleString("es-AR") || "0.00"}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#4CAF50", 0.1),
            color: "#4CAF50",
            "& .MuiChip-icon": { color: "#4CAF50" },
          }}
        />
      ),
    },
    {
      field: "codigo",
      headerName: "Código",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 500,
            borderColor: "rgb(244, 147, 14)",
            color: "rgb(244, 147, 14)",
          }}
        />
      ),
    },
    // Oculta columna "Código de barras"
    // {
    //   field: "codigoScanner",
    //   headerName: "Código de barras",
    //   headerAlign: "center",
    //   flex: 1,
    //   minWidth: 150,
    //   renderCell: (params) => (
    //     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    //       <BarcodeIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
    //       <Typography variant="body2">{params.value}</Typography>
    //     </Box>
    //   ),
    // },
    {
      field: "descripcion",
      headerName: "Descripción",
      headerAlign: "center",
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InventoryIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap fontWeight={"500"}>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "ubicacion",
      headerName: "Destino",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "cliente",
      headerName: "Cliente",
      headerAlign: "center",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "precio",
      headerName: "Precio",
      headerAlign: "center",
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "500", color: "#4CAF50" }}
          >
            ${params.value?.toLocaleString("es-AR") || "0.00"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "pagado",
      headerName: "Pagado",
      headerAlign: "center",
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "500", color: "#4CAF50" }}
          >
            ${params.value?.toLocaleString("es-AR") || "0.00"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "saldo",
      headerName: "Saldo",
      headerAlign: "center",
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "500", color: params.value > 0 ? "#e53e3e" : "#4CAF50" }}
          >
            ${params.value?.toLocaleString("es-AR") || "0.00"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "nroPagos",
      headerName: "Nro de Pagos",
      headerAlign: "center",
      type: "number",
      width: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha("#3182ce", 0.1),
            color: "#3182ce",
          }}
        />
      ),
    },
    {
      field: "porcentajeDescuento",
      headerName: "Desc (%)",
      headerAlign: "center",
      type: "number",
      width: 110,
      flex: 0.7,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: params.value > 0 ? "bold" : "normal",
              color: params.value > 0 ? "#e53e3e" : "inherit",
            }}
          >
            {params.value}%
          </Typography>
        </Box>
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={() => {
                const row = params.row;
                // Adaptar datos mínimos para el modal de cuenta corriente
                const cuentaCorrienteLite = {
                  entidadCompleta: {
                    id: row.idEntidadNumero,
                    descripcion: row.cliente,
                  },
                  tipoEntidad: "Cliente",
                  saldo: 0,
                  // Datos para el tab de cuotas
                  idEntidad: row.idEntidadNumero,
                  idItem: row.idItem,
                  idUbicacion: row.idUbicacion,
                  transacciones: [
                    {
                      id: row.idTransaccion || (row.ventaId && Number(String(row.ventaId).split("-")[1])) || null,
                      fecha: row.fecha,
                      idTipoTransaccion: 1,
                      montoTotal: row.montoTotal,
                      descripcion: row.descripcion,
                    },
                  ],
                  transaccionesItems: [
                    {
                      id: row.idTransaccion || null,
                      fecha: row.fecha,
                      TransaccionItems: [
                        {
                          idItem: row.idItem,
                          item: { descripcion: row.descripcion },
                          cantidad: 0,
                          precio: row.precio,
                          montoTotal: row.montoTotal,
                        },
                      ],
                    },
                  ],
                };
                setSelectedCuentaCorriente(cuentaCorrienteLite);
                setOpenModalVenta(true);
              }}
              sx={{
                color: alpha("#5b5b5f", 0.9),
                "&:hover": { backgroundColor: alpha("#5b5b5f", 0.1) },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Oculta columna "Canal"
  // if (canalesEntidades && canalesEntidades.length > 0) {
  //   baseColumns.splice(-1, 0, {
  //     field: "canal",
  //     headerName: "Canal",
  //     headerAlign: "center",
  //     flex: 1,
  //     minWidth: 120,
  //     renderCell: (params) => (
  //       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //         <ChannelIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
  //         <Typography variant="body2">{params.value || "Sin canal"}</Typography>
  //       </Box>
  //     ),
  //   });
  // }

  return baseColumns;
}, [canalesEntidades]);

  // Función para realizar la consulta usando paginado y filtros.
  const fetchData = async (pageToLoad = page) => {
    try {
      setLoading(true);
      const params = {
        idTipoTransaccion: 1,
        page: pageToLoad,
        limit,
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
        idEntidad: dniCliente?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        idTransaccionSearch: idTransaccionSearch || null,
        canalEntidad: selectedCanalEntidad?.id || null,
      };

      // Se asume que getTransaccionItemByTipoTransaccion devuelve un objeto con:
      // { total, totalPages, currentPage, data: [...] }
      const response = await getTransaccionItemByTipoTransaccion(params);
      console.log("Response:", response); // Para depuración

      if (response) {
        const { data, total, totalPages: tPages, currentPage } = response;
        
        // Extraer descripciones únicas de items para cargar datos completos
        const uniqueDescriptions = [...new Set(data.map(tItem => tItem.item?.descripcion).filter(Boolean))];
        
        // Cargar items completos para obtener atributos
        let itemsCompletos = [];
        if (uniqueDescriptions.length > 0) {
          // Cargar items por descripción (más confiable que por código)
          const itemsPromises = uniqueDescriptions.map(desc => getItemTresLetras(desc));
          const itemsResponses = await Promise.all(itemsPromises);
          itemsCompletos = itemsResponses.flatMap(response => response?.items || []);
        }
        
        // Se construyen las filas para el DataGrid.
        const flattenedRows = data.map((tItem) => {
          const baseDescripcion = tItem.item?.descripcion || "";
          const idItem = tItem.idItem;
          const itemCodigo = tItem.item?.codigo || "";
          
          // Construir atributos visibles como en el buscador
          let atributosStr = "";
          try {
            const activeAttributes = (itemAtributoNoEliminados || []).filter((attr) => !attr.eliminado);
            const itemCompleto = itemsCompletos.find((it) => 
              it.id === idItem || it.codigo === itemCodigo || 
              (it.descripcion && it.descripcion === baseDescripcion)
            );
            
            if (itemCompleto && activeAttributes.length > 0) {
              const attributeValues = activeAttributes
                .map((attr, index) => itemCompleto[`itemDatoAtributo${index + 1}`])
                .filter((v) => v && String(v).trim().length > 0);
              if (attributeValues.length > 0) {
                atributosStr = attributeValues.join(" ");
              }
            }
          } catch (e) {
            // Ignorar errores al formatear atributos
          }
          
          const descripcionConAtributos = atributosStr
            ? `${baseDescripcion} - ${atributosStr}`
            : baseDescripcion;

          return {
            id: `${tItem.Transaccion.id}-${idItem}`,
            // Nro Venta: concatenación de idItem e idTransaccion.
            ventaId: `${idItem}-${tItem.Transaccion.id}`,
            // Se forma la fecha de la transacción.
            fecha: new Date(tItem.Transaccion.fecha).toLocaleDateString("es-AR"),
            idItem,
            codigo: itemCodigo,
            codigoScanner: tItem.item?.codigoScanner || "",
            descripcion: descripcionConAtributos,
            precio: tItem.precio,
            montoTotal: tItem.montoTotal,
            porcentajeDescuento: tItem.porcentajeDescuento,
            cliente:
              tItem.Transaccion.entidad.descripcion ||
              "" + " " + tItem.Transaccion.entidad.apellido ||
              "",
            ubicacion: tItem.Transaccion.ubicacion.descripcion,
            canal: tItem.Transaccion.entidad?.canalEntidad?.descripcion || "",
            // Para ver detalles en el modal - ids mínimos
            idTransaccion: tItem.Transaccion.id,
            idEntidadNumero: tItem.Transaccion.entidad?.id || tItem.Transaccion.idEntidad,
            idUbicacion: tItem.Transaccion.ubicacion?.id || tItem.Transaccion.idUbicacion,
          };
        });

        // Cargar cuotas para cada fila y calcular valores
        const rowsWithCuotas = await Promise.all(
          flattenedRows.map(async (row) => {
            try {
              const cuotasData = await getCuentaCorrienteCuotas(
                row.idEntidadNumero,
                row.idItem,
                row.idUbicacion
              );
              
              const cuotas = cuotasData?.cuotas || [];
              const hoy = new Date();
              
              // Calcular pagado: suma de montos de cuotas pagadas (idTransaccion !== null)
              const pagado = cuotas
                .filter(cuota => cuota.idTransaccion !== null)
                .reduce((sum, cuota) => sum + (cuota.monto || 0), 0);
              
              // Calcular saldo: suma de importes a pagar de cuotas pendientes (con lógica de interés según fechas)
              const saldo = cuotas
                .filter(cuota => cuota.idTransaccion === null)
                .reduce((sum, cuota) => {
                  const montoBase = Number(cuota.montoBase || cuota.monto || 0);
                  const montoInteres = Number(cuota.montoInteres || 0);
                  const fechaSegundoVto = cuota.fechaSegundoVto ? new Date(cuota.fechaSegundoVto) : null;
                  
                  // Si ya pasó el segundo vencimiento, sumar interés
                  const importeAPagar = (fechaSegundoVto && hoy > fechaSegundoVto)
                    ? montoBase + montoInteres
                    : montoBase;
                  
                  return sum + importeAPagar;
                }, 0);
              
              // Calcular nro de pagos: cantidad de cuotas pagadas
              const nroPagos = cuotas.filter(cuota => cuota.idTransaccion !== null).length;
              
              return {
                ...row,
                pagado,
                saldo,
                nroPagos,
              };
            } catch (error) {
              console.error(`Error cargando cuotas para fila ${row.id}:`, error);
              return {
                ...row,
                pagado: 0,
                saldo: 0,
                nroPagos: 0,
              };
            }
          })
        );

        // Calcular el monto total de todos los items
        const totalSum = rowsWithCuotas.reduce(
          (sum, row) => sum + (row.montoTotal || 0),
          0
        );
        setTotalAmount(totalSum);

        setRows(rowsWithCuotas);
        setTotalCount(total);
        setTotalPages(tPages);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Al montar el componente se realiza la consulta inicial.
  useEffect(() => {
    const fetchDataInicio = async () => {
      try {
        await getUbicacion();
        await getItemAtributoNoEliminados();
        
        // Cargar canales de entidad si está habilitado
        const canalEntidad = await getParametrosGlobales(["canalEntidad"])
        console.log("canalEntidad canalEntidad :", canalEntidad.canalEntidad)
        if (canalEntidad.canalEntidad == 1) {
          const datosCanalesEntidades = await getCanalEntidad()
          console.log("datosCanalesEntidades:", datosCanalesEntidades)
          setCanalesEntidades(datosCanalesEntidades)
        }
        
        fetchData(1);
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error)
      }
    };
    
    fetchDataInicio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1); // Llama a la API
    } else {
      setFilteredItems([]); // Vacía las opciones si hay menos de 3 letras
    }
  }, [searchTextCliente]); //Este paramatro searchText condiciona al useEffect, detectando sus cambios. Solo se ejecuta si cambia el valor del input de searchText.

  // Efecto para búsqueda de ítems
  useEffect(() => {
    if (searchText.length >= 3) {
      arrayItemTresLetras([]);
      getItemTresLetras(searchText);
    } else {
      arrayItemTresLetras([]);
      setFilteredItems([]);
    }
  }, [searchText]);

  // Cargar ubicaciones filtradas cuando se selecciona un grupo
  useEffect(() => {
    const cargarUbicacionesFiltradas = async () => {
      if (selectedItem?.id) {
        try {
          const ubicacionesData = await getItemUbicacionFilter(selectedItem.id);
          
          // Mapear las ubicaciones recibidas
          const ubicacionesMapeadas = ubicacionesData.map((itemUbic) => ({
            id: itemUbic.ubicacion.id,
            descripcion: itemUbic.ubicacion.descripcion,
          }));
          
          setUbicacionesFiltradas(ubicacionesMapeadas);
        } catch (error) {
          console.error("Error al cargar ubicaciones filtradas:", error);
          setUbicacionesFiltradas([]);
        }
      } else {
        setUbicacionesFiltradas([]);
      }
    };

    cargarUbicacionesFiltradas();
  }, [selectedItem?.id]);

  // Cuando cambie la página (por la UI de paginado), se vuelve a consultar.
  useEffect(() => {
    if (!isFirstRender.current) {
      fetchData(page);
    } else {
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Verificar si hay filtros activos
  useEffect(() => {
    const hasActiveFilters =
      (endDate && startDate && !endDate.isSame(startDate, "day")) ||
      dniCliente ||
      selectedItem ||
      selectedUbicacion;

    setFiltersActive(hasActiveFilters);
  }, [startDate, endDate, dniCliente, selectedItem, selectedUbicacion]);

  // Al presionar el botón "Buscar" se resetea a la página 1 y se ejecuta el fetch.
  const handleBuscar = () => {
    setPage(1);
    fetchData(1);
  };

  // Función para abrir el modal de gráficas
  const handleOpenGraficasModal = async () => {
    setLoadingGraficas(true);
    setOpenGraficasModal(true);

    try {
      const params = {
        idTipoTransaccion: 1, // Ventas
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
        idEntidad: dniCliente?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        idTransaccionSearch: idTransaccionSearch || null,
        canalEntidad: selectedCanalEntidad?.id || null,
      };

      // Llamar a la nueva función SIN PAGINADO para obtener todos los datos
      const allData = await getTransaccionItemByTipoTransaccionAllData(params);
      console.log("allData ", allData)

      setDatosGraficas(allData.data);
    } catch (error) {
      console.error("Error al cargar datos para gráficas:", error);
      setDatosGraficas([]);
    } finally {
      setLoadingGraficas(false);
    }
  };

  // Función para cerrar el modal de gráficas
  const handleCloseGraficasModal = () => {
    setOpenGraficasModal(false);
    setDatosGraficas([]);
  };

  // Resetear filtros
  const resetFilters = () => {
    setDniCliente(null);
    setSelectedItem(null);
    setSelectedUbicacion(null);
    setSearchText("");
    setSearchTextCliente("");
    setIdTransaccionSearch("");
    setSelectedCanalEntidad(null);

    // Mantener solo la fecha de inicio como hoy - 30 días
    const today = dayjs();
    setStartDate(today.subtract(30, "day"));
    setEndDate(today);

    // Refrescar datos con filtros reseteados
    setPage(1);
    fetchData(1);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#f8f9fa",
          padding: "16px 16px 5px 16px",
          gap: "16px 16px 5px 16px",
        }}
      >


        {/* Contenedor de filtros - Diseño mejorado */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2.5,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Filtro de fechas */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 320,
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Desde"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      inputProps: { maxLength: 10 },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(theme.palette.primary.main, 0.5),
                          },
                        },
                      },
                    },
                  }}
                  sx={{ width: 150 }}
                />
                <DatePicker
                  label="Hasta"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      inputProps: { maxLength: 10 },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(theme.palette.primary.main, 0.5),
                          },
                        },
                      },
                    },
                  }}
                  sx={{ width: 150 }}
                />
              </LocalizationProvider>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 0.3,
                minWidth: 150,
              }}
            >
              <TextField
                label="Buscar por ID"
                type="number"
                size="small"
                value={idTransaccionSearch}
                onChange={(e) => setIdTransaccionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Filtro de cliente */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 250,
              }}
            >
              <Autocomplete
                size="small"
                disablePortal
                options={entidadesClientesTresLetras.map((it) => ({
                  label: `${it.descripcion} ${it.apellido} ${it.dniCuitCuil}`,
                  descripcion: it.descripcion || null,
                  dniCuitCuil: it.dniCuitCuil,
                  id: it.id,
                  apellido: it.apellido,
                  telefono: it.telefono || null,
                  email: it.email || null,
                  direccion: it.direccion || null,
                  localidad: it.localidad || null,
                  provincia: it.provincia || null,
                  idTipoEntidad: it.idTipoEntidad,
                }))}
                value={dniCliente}
                onChange={(event, newValue) => setDniCliente(newValue)}
                onInputChange={(event, newInputValue) =>
                  setSearchTextCliente(newInputValue)
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente - DNI / CUIT / CUIL" />
                )}
              />
            </Box>

            {/* Filtro de producto */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 250,
              }}
            >
              <Autocomplete
                size="small"
                disablePortal
                options={(itemTresLetras || []).map((it) => {
                  // Obtener atributos del item (mismo formato que verGrupos)
                  const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado);
                  const attributeValues = activeAttributes
                    .map((attr, index) => {
                      const attributeValue = it[`itemDatoAtributo${index + 1}`];
                      return attributeValue ? attributeValue : "";
                    })
                    .filter((val) => val.trim() !== "")
                    .join(" ");

                  return {
                    label: `${it.codigo} - ${it.descripcion} - ${attributeValues}`,
                    descripcion: it.descripcion,
                    codigo: it.codigo,
                    id: it.id,
                    codigoScanner: it.codigoScanner,
                    atributos: attributeValues,
                  };
                })}
                value={selectedItem}
                onChange={(event, newValue) => {
                  setSelectedItem(newValue);
                  // Resetear ubicación seleccionada cuando cambia el grupo
                  setSelectedUbicacion(null);
                }}
                onInputChange={(event, newInputValue) =>
                  setSearchText(newInputValue)
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ fontSize: "0.875rem", py: 0.5 }}>
                    <Box>
                      <Box sx={{ fontWeight: 500 }}>
                        {option.descripcion} {option.atributos && option.atributos.trim() !== "" && (
                          <span style={{ fontWeight: 400, color: "#666" }}>• {option.atributos}</span>
                        )}
                      </Box>
                      <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        {option.codigo} • {option.codigoScanner}
                      </Box>
                    </Box>
                  </Box>
                )}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Buscar Grupo" />
                )}
              />
            </Box>

            {/* Filtro de ubicación */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 200,
              }}
            >
              <Autocomplete
                size="small"
                disablePortal
                options={
                  selectedItem && ubicacionesFiltradas.length > 0
                    ? ubicacionesFiltradas.map((it) => ({
                        id: it.id,
                        label: it.descripcion,
                      }))
                    : ubicaciones.map((it) => ({
                        id: it.id,
                        label: it.descripcion,
                      }))
                }
                value={selectedUbicacion}
                onChange={(event, newValue) => {
                  setSelectedUbicacion(newValue);
                }}
                sx={{
                  width: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Destino" />
                )}
              />
            </Box>

            {/* Botones de acción */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleBuscar}
                startIcon={<SearchIcon />}
                disabled={loading}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  textTransform: "none",
                  padding: "8px 20px",
                  color: "white",
                  fontWeight: 500,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                Buscar
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenGraficasModal}
                startIcon={<BarChartIcon />}
                disabled={loading}
                sx={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                  textTransform: "none",
                  padding: "8px 20px",
                  fontWeight: 500,
                  borderRadius: 2,
                  "&:hover": {
                    borderColor: theme.palette.secondary.dark,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                Mostrar Gráficas
              </Button>
              <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
                <IconButton
                  onClick={resetFilters}
                  color={filtersActive ? "secondary" : "primary"}
                  size="medium"
                  sx={{
                    bgcolor: filtersActive
                      ? alpha(theme.palette.secondary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05),
                    "&:hover": {
                      bgcolor: filtersActive
                        ? alpha(theme.palette.secondary.main, 0.2)
                        : alpha(theme.palette.primary.main, 0.1),
                      transform: "rotate(15deg)",
                      transition: "all 0.2s ease-in-out",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {filtersActive ? <FilterAltOffIcon /> : <ReplayIcon />}
                </IconButton>
              </Tooltip>

              {/* Botón Mostrar Más Filtros */}
              <Button
                variant="text"
                size="small"
                onClick={() => setMasFiltrosActive(!masFiltrosActive)}
                startIcon={<TuneIcon />}
                sx={{
                  textTransform: "none",
                  color: "#666",
                  "&:hover": {
                    backgroundColor: alpha("#666", 0.1),
                  },
                }}
              >
                {masFiltrosActive ? "Ocultar Filtros" : "Mostrar Más Filtros"}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Sección de Filtros Adicionales */}
        {masFiltrosActive && (
          <Paper
            sx={{
              p: 2,
              m: 1,
              borderRadius: 3,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
              Filtros adicionales
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              {canalesEntidades && canalesEntidades.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: { xs: "100%", md: 300 },
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Canal de entidad:
                  </Typography>
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={[
                      { id: null, label: "Todos los canales" },
                      ...canalesEntidades.map((canal) => ({
                        id: canal.id,
                        label: canal.descripcion || `Canal ${canal.id}`,
                      })),
                    ]}
                    value={
                      selectedCanalEntidad
                        ? {
                            id: selectedCanalEntidad.id,
                            label: selectedCanalEntidad.label,
                          }
                        : { id: null, label: "Todos los canales" }
                    }
                    onChange={(event, newValue) => setSelectedCanalEntidad(newValue?.id ? newValue : null)}
                    sx={{ flex: 1, minWidth: 150 }}
                    renderInput={(params) => <TextField {...params} label="Seleccionar canal" variant="outlined" />}
                  />
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Contenedor de la tabla (scrollable) */}
        <Paper
          sx={{
            flex: 1,
            m: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            height: "500px",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            paginationMode="server"
            getRowHeight={() => 35}
            headerHeight={35}
            // DataGrid usa 0-based index para páginas, por lo que se ajusta.
            onPageChange={(newPage) => setPage(newPage + 1)}
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        {/* Contenedor del footer (paginación) */}
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            backgroundColor: "#ffffff",
            borderRadius: 3,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(0,0,0,0.03)",
          }}
        >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={totalAmount}
            vieneDe={"tablaDetalle"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>
      </Box>

      {/* Modal de gráficas */}
      <GraficasDetallesVentasModal
        open={openGraficasModal}
        handleClose={handleCloseGraficasModal}
        data={datosGraficas}
        loading={loadingGraficas}
      />

      {/* Modal de detalles (mismo que en TablaCuentaCorriente) */}
      {openModalVenta && (
        <ModalDetalleCuentaCorriente
          open={openModalVenta}
          handleClose={handleCloseModal}
          cuentaCorriente={selectedCuentaCorriente}
        />
      )}
    </ThemeProvider>
  );
};

export default TablaDetallesVentas;
